<div class="w-full relative">
  <!-- Page Header -->

  <div class="relative">
    @if (isLocked) {
      <div class="absolute inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm rounded-t-2xl">
        <div class="p-6 rounded-2xl bg-info-light border border-info-border flex flex-col items-center gap-4 max-w-md text-center">
          <span class="material-icons text-info text-4xl">lock</span>
          <span class="text-info font-medium">
            @if (lockInfo?.employeeId) {
              Dieser Lieferzettel wird bereits durch den Benutzer mit Mitarbeiter-Nr.: {{ lockInfo?.employeeId }} bearbeitet. Bearbeitung ist nicht
              möglich.
            } @else {
              Dieser Lieferzettel wird bereits durch einen anderen Benutzer bearbeitet. Bearbeitung ist nicht möglich.
            }
          </span>
          <button type="button" class="btn-secondary mt-2" (click)="cancel()">Zurück zur Übersicht</button>
        </div>
      </div>
    }

    <!-- Loading State -->
    @if (isLoading) {
      <div class="flex items-center justify-center py-20">
        <div class="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    } @else {
      <form [formGroup]="deliveryNoteForm" (ngSubmit)="submit()">
        <!-- Order Info Header -->
        <div class="glass-card !z-40 p-6 rounded-2xl mb-6 order-info-card">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-text-primary">Auftragsinformationen</h2>
            @if (isEditMode && deliveryNoteId) {
              <button type="button" class="btn-secondary flex items-center gap-2" [disabled]="isExporting" (click)="exportPdf()">
                @if (isExporting) {
                  <span class="animate-spin rounded-full h-4 w-4 border-2 border-text-muted border-t-transparent"></span>
                  <span>Exportieren...</span>
                } @else {
                  <span class="material-icons text-lg">picture_as_pdf</span>
                  <span>PDF Export</span>
                }
              </button>
            }
          </div>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
            <!-- Order Selection -->
            <div class="form-group">
              <label for="order" class="form-label">Auftrag *</label>
              <div class="relative" (click)="$event.stopPropagation()">
                <input
                  id="order"
                  type="text"
                  class="form-input pr-10"
                  [class.error]="isFieldInvalid('order')"
                  [value]="orderSearchValue"
                  (input)="onOrderSearchInput($event)"
                  (focus)="openOrderDropdown()"
                  (blur)="onOrderBlur()"
                  (keydown)="onOrderSearchKeydown($event)"
                  placeholder="Suche nach Auftrags-Nr. oder Kunde..."
                  autocomplete="off"
                  appSelectOnFocus
                />

                @if (deliveryNoteForm.get('order')?.value || orderSearchValue) {
                  <button type="button" class="order-clear-btn" title="Auswahl entfernen" (click)="clearSelectedOrder(); $event.stopPropagation()">
                    <span class="material-icons text-xl">close</span>
                  </button>
                }

                @if (showOrderDropdown) {
                  <div class="order-dropdown">
                    @if (orderSearchValue.trim().length < minOrderSearchLength) {
                      <div class="order-dropdown-empty">Bitte mindestens {{ minOrderSearchLength }} Zeichen eingeben...</div>
                    } @else if (isOrderSearchLoading) {
                      <div class="order-dropdown-empty flex items-center gap-2">
                        <span class="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></span>
                        Suche...
                      </div>
                    } @else if (filteredOrders.length === 0) {
                      <div class="order-dropdown-empty">Keine Ergebnisse</div>
                    } @else {
                      @for (order of filteredOrders; track order._id; let i = $index) {
                        <button
                          type="button"
                          class="order-dropdown-item"
                          [class.active]="i === highlightedOrderIndex"
                          (mouseenter)="highlightedOrderIndex = i"
                          (click)="selectOrder(order)"
                        >
                          <div class="flex items-center justify-between gap-3">
                            <span class="font-medium text-text-primary">{{ order.internalOrderId }}</span>
                            <span class="text-text-secondary truncate">{{ order.companyName }}</span>
                          </div>
                        </button>
                      }
                    }
                  </div>
                }
              </div>
              @if (isFieldInvalid('order')) {
                <span class="form-error">Bitte wählen Sie einen Auftrag aus</span>
              }
            </div>

            <!-- Employee ID (readonly) -->
            <div class="form-group">
              <label for="employeeId" class="form-label">Mitarbeiter-Nr.</label>
              <div class="relative">
                <input type="text" id="employeeId" formControlName="employeeId" class="form-input" [class.pr-10]="isAdmin" readonly />
                @if (isAdmin) {
                  <app-employee-info-button [employeeId]="deliveryNoteForm.get('employeeId')?.value" />
                }
              </div>
            </div>

            <!-- Status -->
            <div class="form-group">
              <label for="status" class="form-label">Status *</label>
              <p-select
                inputId="status"
                formControlName="status"
                [options]="statusOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Status wählen"
                [filter]="false"
                [showClear]="false"
                styleClass="select-component-style"
                panelStyleClass="select-component-panel-style"
                (onChange)="onStatusChange($event.value)"
              >
                <ng-template pTemplate="item" let-option>
                  <div class="option-row">
                    <span class="option-label">{{ option.label }}</span>
                  </div>
                </ng-template>
              </p-select>
            </div>

            <!-- Versandart -->
            <div class="form-group">
              <label for="shippingType" class="form-label">Versandart *</label>
              <p-select
                inputId="shippingType"
                formControlName="shippingType"
                [options]="shippingTypeOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Versandart wählen"
                [filter]="false"
                [showClear]="false"
                styleClass="select-component-style"
                panelStyleClass="select-component-panel-style"
              >
                <ng-template pTemplate="item" let-option>
                  <div class="option-row">
                    <span class="option-label">{{ option.label }}</span>
                  </div>
                </ng-template>
              </p-select>
            </div>
          </div>
        </div>

        <!-- Positions Table -->
        <div class="table-wrapper glass-card rounded-2xl mb-6 overflow-hidden">
          <div class="p-6 border-b border-white/10 flex items-center justify-between">
            <h2 class="text-lg font-semibold text-text-primary">Positionen</h2>
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="btn-secondary flex justify-center"
                [ngClass]="isHeaderPinned ? 'text-primary bg-primary/15' : 'text-text-muted hover:text-text-primary hover:bg-white/10'"
                (click)="toggleHeaderPin()"
                [title]="isHeaderPinned ? 'Kopfzeile lösen' : 'Kopfzeile fixieren'"
              >
                <span class="material-icons text-lg">push_pin</span>
              </button>
              <button type="button" class="btn-primary flex items-center gap-2" (click)="addPositionAndFocus()">
                <span class="material-icons text-lg">add</span>
                Zeile hinzufügen
              </button>
            </div>
          </div>

          <div [ngClass]="isHeaderPinned ? 'overflow-auto max-h-[calc(100vh-14rem)] overscroll-contain' : 'overflow-x-auto'">
            <table class="w-full">
              <thead>
                <tr class="border-b border-white/10" [ngClass]="{ 'sticky-header': isHeaderPinned }">
                  <th class="position-header">Artikel / Pos.</th>
                  <th class="position-header">Rollen</th>
                  <th class="position-header">Stck a'</th>
                  <th class="position-header">Ges. STK</th>
                  <th class="position-header">Folie 1</th>
                  <th class="position-header">Folie N</th>
                  <th class="position-header">Folie A</th>
                  <th class="position-header">Dry P.</th>
                  <th class="position-header">Um. Kt.</th>
                  <th class="position-header">Am. P.</th>
                  <th class="position-header">Kt.</th>
                  <th class="position-header">kg</th>
                  <th class="position-header">Bemerkung</th>
                  <th class="position-header"></th>
                </tr>
              </thead>
              <tbody formArrayName="positions">
                @for (position of positions.controls; track $index; let i = $index) {
                  <tr [formGroupName]="i" class="border-b border-white/10">
                    <!-- Article / Position (manual entry) -->
                    <td class="position-cell">
                      <div class="flex items-center gap-2">
                        <span>Pos.</span>
                        <input
                          type="number"
                          formControlName="articlePosition"
                          class="position-input"
                          [class.error]="isPositionFieldInvalid(i, 'articlePosition')"
                          placeholder="z.B Pos. 1"
                          appSelectOnFocus
                        />
                      </div>
                    </td>

                    <!-- Roll Count -->
                    <td class="position-cell">
                      <input type="number" formControlName="rollCount" class="position-input" min="0" appSelectOnFocus />
                    </td>

                    <!-- Pieces Per Roll -->
                    <td class="position-cell">
                      <input type="number" formControlName="piecesPerRoll" class="position-input" min="0" appSelectOnFocus />
                    </td>

                    <!-- Total Pieces (calculated, readonly) -->
                    <td class="position-cell">
                      <div class=" ">
                        <input type="number" formControlName="totalPieces" class="position-input bg-white/4" readonly />
                        @if (isLastOccurrenceArticlePosition(i)) {
                          <span class="mt-1 text-[14px] leading-tight text-text-muted">
                            Sum. Pos. {{ positions.at(i).get('articlePosition')?.value }}:
                            <span class="text-text-primary">{{ getTotalPiecesForArticlePosition(i) }}</span>
                          </span>
                        }
                      </div>
                    </td>

                    <!-- Folie 1 -->
                    <td class="position-cell">
                      <input type="number" formControlName="folie1" class="position-input" min="0" appSelectOnFocus />
                    </td>

                    <!-- Folie N -->
                    <td class="position-cell">
                      <input type="number" formControlName="folieN" class="position-input" min="0" appSelectOnFocus />
                    </td>

                    <!-- Folie A -->
                    <td class="position-cell">
                      <input type="number" formControlName="folieA" class="position-input" min="0" appSelectOnFocus />
                    </td>

                    <!-- Dry P. -->
                    <td class="position-cell">
                      <input type="number" formControlName="dryP" class="position-input" min="0" appSelectOnFocus />
                    </td>

                    <!-- Um. Kt. -->
                    <td class="position-cell">
                      <input type="number" formControlName="umCarton" class="position-input" min="0" appSelectOnFocus />
                    </td>

                    <!-- Am. P. -->
                    <td class="position-cell">
                      <input type="number" formControlName="ammopack" class="position-input" min="0" appSelectOnFocus />
                    </td>

                    <!-- Carton Count -->
                    <td class="position-cell">
                      <input type="number" formControlName="cartonCount" class="position-input" min="0" appSelectOnFocus />
                    </td>

                    <!-- Weight -->
                    <td class="position-cell">
                      <input type="number" formControlName="weightKg" class="position-input" min="0" step="0.1" appSelectOnFocus />
                    </td>

                    <!-- Bemerkung -->
                    <td class="position-cell w-80">
                      <div class="relative group">
                        <textarea
                          [title]="position.get('note')?.value"
                          [id]="'note-' + i"
                          class="position-input position-textarea !pr-4"
                          formControlName="note"
                          cdkTextareaAutosize
                          cdkAutosizeMinRows="1"
                          cdkAutosizeMaxRows="100"
                          appSelectOnFocus
                        ></textarea>
                        <button
                          type="button"
                          class="absolute cursor-pointer top-1 right-1 text-text-muted hover:text-text-primary transition-colors opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 p-1"
                          (click)="openTextBlockModalForField('note-' + i)"
                          title="Textbaustein einfügen"
                        >
                          <span class="material-icons text-xs">text_snippet</span>
                        </button>
                      </div>
                    </td>

                    <!-- Delete Button -->
                    <td class="position-cell">
                      @if (positions.length > 1) {
                        <button
                          type="button"
                          class="p-1.5 rounded-lg flex items-center justify-center text-red-400 hover:text-red-300 hover:bg-red-400/10 transition-all"
                          (click)="removePosition(i)"
                          title="Position entfernen"
                        >
                          <span class="material-icons text-lg">close</span>
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>

              <!-- Summary Row -->
              <tfoot>
                <tr class="bg-white/4 border-t border-white/10">
                  <td class="position-cell font-semibold text-text-primary">Summe</td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary">{{ totalRollCount }}</span>
                  </td>
                  <td class="position-cell"></td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary"></span>
                  </td>

                  <td class="position-cell">
                    <span class="font-semibold text-blue-400">{{ totalFolie1 }} Stk.</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-yellow-400">{{ totalFolieN }} Stk.</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-green-400">{{ totalFolieA }} Stk.</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-purple-400">{{ totalDryP }} Stk.</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary">{{ totalUmCarton }} Stk.</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary">{{ totalAmmopack }} Stk.</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary">{{ totalCartonCount }} Stk.</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary">{{ totalWeight }} Kg</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary"></span>
                  </td>
                  <td class="position-cell"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Additional Info -->
        <div class="glass-card p-6 rounded-2xl mb-6">
          <div class="flex items-end gap-4 mb-4">
            <h2 class="text-lg font-semibold text-text-primary">Zusätzliche Informationen</h2>
            <button
              type="button"
              class="text-text-muted cursor-pointer hover:text-text-primary transition-colors"
              (click)="openTextBlockModalForField('additionalInfo')"
              title="Textbaustein einfügen"
            >
              <span class="material-icons !text-2xl">text_snippet</span>
            </button>
          </div>

          <div class="form-group">
            <textarea
              id="additionalInfo"
              formControlName="additionalInfo"
              class="form-input min-h-[120px]"
              appSelectOnFocus
              placeholder="Zusätzliche Informationen, Rückverpackung, etc..."
            ></textarea>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-end gap-4">
          <button type="button" class="btn-secondary" (click)="cancel()">Schließen</button>
          <button type="submit" class="btn-primary px-6 py-3" [disabled]="isSubmitting">
            @if (isSubmitting) {
              <span class="flex items-center gap-2">
                <span class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                Speichern...
              </span>
            } @else {
              <span class="flex items-center gap-2">
                <span class="material-icons text-lg">save</span>
                {{ isEditMode ? 'Speichern' : 'Erstellen' }}
              </span>
            }
          </button>
        </div>
      </form>
    }
  </div>
</div>
